// src/pages/Tickets/TicketStatusList.jsx
// -----------------------------------------------------------------------------
// Component screen managing Ticket Statuses (Ticket_Status_TBL)
// Performs full CRUD (Read, Create, Update, Delete) via ticketStatusService.
// -----------------------------------------------------------------------------
import { useEffect, useState, useMemo } from 'react';
import { ticketStatusService, projectService } from '../../api';
import useTableData from '../../hooks/useTableData';
import TableList from '../../components/common/TableList';
import TableForm from '../../components/common/TableForm';
import DeleteConfirmModal from '../../components/common/DeleteConfirmModal';
import StatusBadge from '../../components/common/StatusBadge';

export default function TicketStatusList() {
  const [projects, setProjects] = useState([]);

  // Fetch projects to populate the Project ID dropdown options
  useEffect(() => {
    projectService
      .getAll()
      .then((res) => setProjects(res.data || []))
      .catch(() => {});
  }, []);

  const {
    data,
    paginatedData,
    loading,
    submitting,
    deleting,
    serverErrors,
    searchTerm,
    setSearchTerm,
    sortConfig,
    handleSort,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalPages,
    totalRecords,
    isFormOpen,
    isDeleteOpen,
    formMode,
    selectedRecord,
    openCreateModal,
    openEditModal,
    closeFormModal,
    openDeleteModal,
    closeDeleteModal,
    handleSave,
    handleDelete,
  } = useTableData(ticketStatusService, {
    idKey: 'statusID',
    itemName: 'Ticket Status',
    initialSort: { key: 'sequenceOrder', direction: 'asc' },
  });

  // Schema configuration for Ticket_Status_TBL
  const statusFields = useMemo(() => [
    {
      name: 'statusID',
      label: 'ID',
      type: 'integer',
      isPrimaryKey: true,
      readOnly: true,
    },
    {
      name: 'projectID',
      label: 'Project',
      type: 'select',
      required: true,
      options: projects.map((p) => ({ label: `${p.projectCode} — ${p.projectName}`, value: p.projectID })),
    },
    {
      name: 'statusName',
      label: 'Status Name',
      type: 'varchar',
      required: true,
      maxLength: 50,
      placeholder: 'e.g. Open, In Progress, QA Testing, Resolved',
    },
    {
      name: 'sequenceOrder',
      label: 'Sequence Order',
      type: 'integer',
      required: true,
      min: 0,
      placeholder: 'e.g. 1, 2, 3...',
    },
    {
      name: 'isClosedStatusFlag',
      label: 'Is Closed Status Flag',
      type: 'checkbox',
      required: false,
      placeholder: 'Flag whether this status marks the ticket as closed',
      defaultValue: false,
    },
  ], [projects]);

  const columns = [
    { key: 'statusID', label: 'ID', sortable: true, width: '80px' },
    {
      key: 'projectID',
      label: 'Project',
      sortable: true,
      render: (row) => {
        const proj = projects.find((p) => Number(p.projectID) === Number(row.projectID));
        return proj ? proj.projectName : `Project #${row.projectID}`;
      },
    },
    { key: 'statusName', label: 'Status Name', sortable: true },
    { key: 'sequenceOrder', label: 'Sequence Order', sortable: true, width: '150px' },
    {
      key: 'isClosedStatusFlag',
      label: 'Is Closed Status',
      sortable: true,
      render: (row) => {
        const isClosed = row.isClosedStatusFlag === true || String(row.isClosedStatusFlag) === 'true';
        return (
          <StatusBadge
            value={isClosed ? 'Yes' : 'No'}
            customClass={isClosed ? 'badge-danger' : 'badge-info'}
          />
        );
      },
    },
  ];

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Ticket Statuses</h1>
          <div className="subtitle">
            Configure ticket workflows and closed status settings per project.
          </div>
        </div>
        <button className="btn btn-primary" onClick={openCreateModal}>
          + New Status
        </button>
      </div>

      <TableList
        columns={columns}
        data={data}
        paginatedData={paginatedData}
        loading={loading}
        idKey="statusID"
        emptyMessage="No statuses found."
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        sortConfig={sortConfig}
        onSort={handleSort}
        currentPage={currentPage}
        totalPages={totalPages}
        totalRecords={totalRecords}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
        onEdit={openEditModal}
        onDelete={openDeleteModal}
      />

      {isFormOpen && (
        <TableForm
          fields={statusFields}
          initialValues={selectedRecord || { projectID: '', statusName: '', sequenceOrder: '', isClosedStatusFlag: false }}
          mode={formMode}
          isOpen={isFormOpen}
          onSubmit={handleSave}
          onCancel={closeFormModal}
          submitting={submitting}
          serverErrors={serverErrors}
          itemName="Ticket Status"
        />
      )}

      <DeleteConfirmModal
        isOpen={isDeleteOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
        title="Delete Status"
        itemLabel={selectedRecord ? selectedRecord.statusName : 'Ticket Status'}
        deleting={deleting}
      />
    </div>
  );
}
